import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm, 
  Row, 
  Col,
  Tag,
  Upload,
  Divider,
  Statistic,
  Tooltip,
  Drawer,
  Descriptions,
  Grid,
  Dropdown,
  Pagination
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UploadOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';


const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;



interface ShippingStation {
  id: number;
  name: string;
  address: string;
  contact_person: string;
  phone: string;
  mobile: string;
  status: '开启' | '关闭';
  remark: string;
}

interface ImportResult {
  success: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; reason: string }>;
}

const ShippingStationManagement: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [messageApi, contextHolder] = message.useMessage();
  
  const [data, setData] = useState<ShippingStation[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingStation, setEditingStation] = useState<ShippingStation | null>(null);
  const [selectedStation, setSelectedStation] = useState<ShippingStation | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [requiredFields, setRequiredFields] = useState<string[]>(['name', 'address', 'contact_person']); // 默认必填字段
  const [importData, setImportData] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [searchText, setSearchText] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // 获取收发货站点列表
  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/shipping-stations?current=${current}&pageSize=${pageSize}&${new URLSearchParams(params)}`);
      const result = await response.json();
      if (result.code === 0) {
        setData(result.data);
        setTotal(result.total);
      } else {
        messageApi.error({
          content: '获取站点数据失败，请刷新页面重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，无法获取站点数据，请检查网络连接',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  }, [current, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 搜索
  const handleSearch = useCallback((searchValue?: string) => {
    const searchTextToUse = searchValue !== undefined ? searchValue : searchText;
    if (searchTextToUse.trim()) {
      const params = { search: searchTextToUse.trim() };
      setCurrent(1);
      fetchData(params);
    } else {
      // 如果搜索文本为空，则获取所有数据
      setCurrent(1);
      fetchData();
    }
  }, [fetchData, setCurrent, searchText]);

  // 重置搜索
  const handleReset = useCallback(() => {
    searchForm.resetFields();
    setSearchText('');
    setCurrent(1);
    fetchData();
  }, [fetchData, searchForm, setCurrent]);

  // 分页变化处理
  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrent(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // 提交表单
  const handleSubmit = useCallback(async (values: any) => {
    try {
      if (editingStation) {
        // 更新
        const response = await fetch(`/api/shipping-stations/${editingStation.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        if (result.success) {
          messageApi.success({
            content: '站点信息更新成功！',
            duration: 3,
          });
          setModalVisible(false);
          fetchData();
        } else {
          messageApi.error({
            content: result.message || '站点信息更新失败，请重试',
            duration: 4,
          });
        }
      } else {
        // 创建
        const response = await fetch('/api/shipping-stations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        if (result.success) {
          messageApi.success({
            content: '站点信息创建成功！',
            duration: 3,
          });
          setModalVisible(false);
          fetchData();
        } else {
          messageApi.error({
            content: result.message || '站点信息创建失败，请重试',
            duration: 4,
          });
        }
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，操作失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [editingStation, fetchData, messageApi]);

  // 删除
  const handleDelete = useCallback(async (id: number) => {
    try {
      const response = await fetch(`/api/shipping-stations/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.success) {
        messageApi.success({
          content: '站点信息删除成功！',
          duration: 3,
        });
        fetchData();
      } else {
        messageApi.error({
          content: result.message || '站点信息删除失败，请重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，删除失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [fetchData, messageApi]);

  // 编辑
  const handleEdit = useCallback((record: ShippingStation) => {
    setEditingStation(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  }, [form, setModalVisible]);

  // 新增
  const handleAdd = useCallback(() => {
    setEditingStation(null);
    form.resetFields();
    // 设置默认值
    form.setFieldsValue({
      status: '开启'
    });
    setModalVisible(true);
  }, [form, setModalVisible]);

  // 查看站点详情
  const handleView = useCallback(async (station: ShippingStation) => {
    setSelectedStation(station);
    setDrawerVisible(true);
  }, [setSelectedStation, setDrawerVisible]);

  // 文件上传处理
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const data = lines.slice(1).filter(line => line.trim()).map((line, index) => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          row._rowIndex = index + 1;
          return row;
        });

        // 设置默认字段映射
        const defaultMapping: Record<string, string> = {};
        headers.forEach(header => {
          if (header.toLowerCase().includes('名称') || header.toLowerCase().includes('name')) {
            defaultMapping[header] = 'name';
          } else if (header.toLowerCase().includes('地址') || header.toLowerCase().includes('address')) {
            defaultMapping[header] = 'address';
          } else if (header.toLowerCase().includes('联系人') || header.toLowerCase().includes('contact')) {
            defaultMapping[header] = 'contact_person';
          } else if (header.toLowerCase().includes('电话') || header.toLowerCase().includes('phone')) {
            defaultMapping[header] = 'phone';
          } else if (header.toLowerCase().includes('手机') || header.toLowerCase().includes('mobile')) {
            defaultMapping[header] = 'mobile';
          } else if (header.toLowerCase().includes('状态') || header.toLowerCase().includes('status')) {
            defaultMapping[header] = 'status';
          } else if (header.toLowerCase().includes('备注') || header.toLowerCase().includes('remark')) {
            defaultMapping[header] = 'remark';
          }
        });

        setFieldMapping(defaultMapping);
        setImportData(data);
        setImportModalVisible(true);
      } catch (error) {
        messageApi.error({
          content: '文件解析失败，请检查文件格式是否正确',
          duration: 4,
        });
      }
    };
    reader.readAsText(file);
    return false; // 阻止自动上传
  }, []);

    // 导出CSV文件
  const exportToCSV = useCallback(async () => {
    setExportLoading(true);
    try {
      // 获取所有数据用于导出
      const response = await fetch('/api/shipping-stations?current=1&pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      // 准备导出数据
      const exportData = result.data.map((station: ShippingStation) => ({
        '站点名称': station.name,
        '地址': station.address,
        '联系人': station.contact_person,
        '固定电话': station.phone || '',
        '手机号码': station.mobile || '',
        '状态': station.status,
        '备注': station.remark || ''
      }));

      // 创建CSV内容
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map((row: Record<string, any>) => 
          headers.map(header => `"${row[header] || ''}"`).join(',')
        )
      ].join('\n');

      // 创建下载链接
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `收发货站点_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      messageApi.success({
        content: 'CSV文件导出成功！',
        duration: 3,
      });
    } catch (error) {
      messageApi.error({
        content: 'CSV文件导出失败，请重试',
        duration: 4,
      });
    } finally {
      setExportLoading(false);
    }
  }, []);

  // 导出Excel文件
  const exportToExcel = useCallback(async () => {
    setExportLoading(true);
    try {
      // 获取所有数据用于导出
      const response = await fetch('/api/shipping-stations?current=1&pageSize=10000');
      const result = await response.json();
      
      if (result.code !== 0) {
        throw new Error('获取导出数据失败');
      }
      
      // 准备导出数据
      const exportData = result.data.map((station: ShippingStation) => ({
        '站点名称': station.name,
        '地址': station.address,
        '联系人': station.contact_person,
        '固定电话': station.phone || '',
        '手机号码': station.mobile || '',
        '状态': station.status,
        '备注': station.remark || ''
      }));

      // 创建工作簿和工作表
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // 设置列宽
      const colWidths = [
        { wch: 15 }, // 站点名称
        { wch: 30 }, // 地址
        { wch: 10 }, // 联系人
        { wch: 15 }, // 固定电话
        { wch: 15 }, // 手机号码
        { wch: 8 },  // 状态
        { wch: 20 }  // 备注
      ];
      worksheet['!cols'] = colWidths;

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(workbook, worksheet, '收发货站点');

      // 导出文件
      XLSX.writeFile(workbook, `收发货站点_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      messageApi.success({
        content: 'Excel文件导出成功！',
        duration: 3,
      });
    } catch (error) {
      messageApi.error({
        content: 'Excel文件导出失败，请重试',
        duration: 4,
      });
    } finally {
      setExportLoading(false);
    }
  }, []);

  // 执行导入
  const handleImport = useCallback(async () => {
    try {
      // 验证必填字段映射
      const missingRequiredFields = requiredFields.filter(field => 
        !Object.values(fieldMapping).includes(field)
      );
      
      if (missingRequiredFields.length > 0) {
        messageApi.error({
          content: `缺少必填字段映射：${missingRequiredFields.join(', ')}`,
          duration: 4,
        });
        return;
      }

      const mappedData = importData.map((row, index) => {
        const mapped: any = {};
        Object.keys(fieldMapping).forEach(sourceField => {
          const targetField = fieldMapping[sourceField];
          if (targetField && targetField !== 'skip') {
            mapped[targetField] = row[sourceField];
          }
        });
        mapped._rowIndex = index + 1; // 添加行号用于错误定位
        return mapped;
      });

      // 验证必填字段数据
      const validationErrors: Array<{ row: number; reason: string }> = [];
      mappedData.forEach(item => {
        requiredFields.forEach(field => {
          if (!item[field] || item[field].toString().trim() === '') {
            validationErrors.push({
              row: item._rowIndex,
              reason: `必填字段"${field}"为空`
            });
          }
        });
      });

      // 过滤掉有验证错误的记录
      const validData = mappedData.filter(item => 
        !validationErrors.some(error => error.row === item._rowIndex)
      );

      // 发送到后端导入
      const response = await fetch('/api/shipping-stations/batch-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: validData }),
      });

      const result = await response.json();
      
      console.log('后端返回结果:', result); // 调试用
      console.log('前端验证结果:', { validationErrors, validData }); // 调试用
      
      // 构建导入结果 - 使用后端返回的真实数据
      const importResult: ImportResult = {
        success: result.success ? (result.successCount || 0) : 0, // 使用后端返回的成功数量
        skipped: result.success ? (result.skipCount || 0) : 0, // 使用后端返回的跳过数量
        failed: validationErrors.length + (result.success ? (result.errorCount || 0) : 0), // 前端验证错误 + 后端处理错误
        errors: [
          ...validationErrors, // 前端验证错误
          ...(result.success && result.errors ? result.errors.map((err: any) => ({
            row: err.row,
            reason: err.message
          })) : []) // 后端处理错误
        ]
      };

      setImportResult(importResult);
      
      if (result.success) {
        messageApi.success({
          content: `导入完成！成功导入 ${importResult.success} 条记录`,
          duration: 4,
        });
        // 强制刷新数据列表
        setTimeout(() => {
          fetchData();
        }, 500);
      } else {
        messageApi.error({
          content: result.message || '导入失败，请检查数据格式后重试',
          duration: 4,
        });
      }
    } catch (error) {
      messageApi.error({
        content: '网络错误，导入失败，请检查网络连接后重试',
        duration: 4,
      });
    }
  }, [fieldMapping, importData, requiredFields, fetchData]);

  // 过滤站点列表
  const filteredStations = useMemo(() => data.filter(station =>
    (station.name && station.name.toLowerCase().includes(searchText.toLowerCase())) ||
    (station.contact_person && station.contact_person.toLowerCase().includes(searchText.toLowerCase())) ||
    (station.phone && station.phone.includes(searchText)) ||
    (station.mobile && station.mobile.includes(searchText))
  ), [data, searchText]);

  // 渲染站点卡片（移动端）
  const renderStationCard = useCallback((station: ShippingStation) => (
    <Card 
      key={station.id} 
      size="small" 
      style={{ marginBottom: 12 }}
      actions={[
        <Tooltip title="查看详情" key="view">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleView(station)}
          >
            详情
          </Button>
        </Tooltip>,
        <Tooltip title="编辑" key="edit">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(station)}
          >
            编辑
          </Button>
        </Tooltip>,
        <Popconfirm
          title="确定要删除这个站点吗？"
          onConfirm={() => handleDelete(station.id)}
          okText="确定"
          cancelText="取消"
          key="delete"
        >
          <Tooltip title="删除">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Tooltip>
        </Popconfirm>
      ]}
    >
      <Row gutter={[8, 6]}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">站点名称：</Text>
            <div style={{ textAlign: 'right' }}>
              <Text strong style={{ fontSize: 16 }}>{station.name}</Text>
              <Tag color={station.status === '开启' ? 'green' : 'red'} style={{ marginLeft: 6 }}>
                {station.status}
              </Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">地址：</Text>
            <Text style={{ textAlign: 'right' }}>{station.address}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">联系人：</Text>
            <Text style={{ textAlign: 'right' }}>{station.contact_person}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">固定电话：</Text>
            <Text copyable style={{ textAlign: 'right' }}>{station.phone || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">手机号码：</Text>
            <Text copyable style={{ textAlign: 'right' }}>{station.mobile || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Text type="secondary">备注：</Text>
            <div style={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{station.remark || '无'}</Text>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  ), [handleView, handleEdit, handleDelete]);

  // 表格列定义
  const columns = useMemo(() => [
    {
      title: '站点名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
      fixed: 'left' as const,
      width: isMobile ? 100 : 150
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: isMobile ? 120 : 200,
      ellipsis: true
    },
    {
      title: '联系人',
      dataIndex: 'contact_person',
      key: 'contact_person',
      width: isMobile ? 80 : 100
    },
    {
      title: '固定电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
      width: isMobile ? 100 : 120
    },
    {
      title: '手机号码',
      dataIndex: 'mobile',
      key: 'mobile',
      render: (mobile: string) => mobile || '-',
      width: isMobile ? 100 : 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === '开启' ? 'green' : 'red'}>
          {status}
        </Tag>
      ),
      width: isMobile ? 60 : 80
    },

    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      render: (remark: string) => (
        <div style={{ 
          maxWidth: isMobile ? 60 : 120, 
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          lineHeight: '1.4'
        }}>
          {remark || '-'}
        </div>
      ),
      width: isMobile ? 80 : 150,
      responsive: ['lg'] as any
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: isMobile ? 120 : 150,
      render: (_: any, record: ShippingStation) => (
        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
          <Tooltip title="查看详情">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个站点吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size={isMobile ? 'small' : 'middle'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ], [isMobile, handleView, handleEdit, handleDelete]);

  return (
    <div style={{ padding: isMobile ? '16px' : '12px' }}>
      {contextHolder}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={8}>
              <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>收发货站点管理</Title>
            </Col>
            <Col xs={24} sm={24} md={16}>
              <Space 
                direction={isMobile ? 'vertical' : 'horizontal'} 
                style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
                size="small"
              >
                <Input
                  placeholder="搜索站点名称、联系人或电话"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchText(value);
                    // 输入即搜索，延迟300ms避免频繁请求
                    if (searchTimeout.current) {
                      clearTimeout(searchTimeout.current);
                    }
                    searchTimeout.current = setTimeout(() => {
                      handleSearch(value);
                    }, 300);
                  }}
                  allowClear
                  style={{ width: isMobile ? '100%' : 250 }}
                />
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchData}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  刷新
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => setImportModalVisible(true)}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  批量导入
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: '导出CSV',
                        onClick: exportToCSV,
                        icon: <DownloadOutlined />
                      },
                      {
                        key: 'excel',
                        label: '导出Excel',
                        onClick: exportToExcel,
                        icon: <DownloadOutlined />
                      }
                    ]
                  }}
                >
                  <Button 
                    icon={<DownloadOutlined />} 
                    loading={exportLoading}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    导出
                  </Button>
                </Dropdown>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAdd}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  新增站点
                </Button>
              </Space>
            </Col>
          </Row>
          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                共 {total} 个站点（当前页显示 {data.length} 个）
              </Text>
            </div>
          )}
        </div>

        {isMobile ? (
          // 移动端卡片布局
          <div>
            {data.map(renderStationCard)}
            {data.length === 0 && !loading && (
              <Card style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">暂无站点数据</Text>
              </Card>
            )}
            
            {/* 移动端分页 */}
            {total > pageSize && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Pagination
                  current={current}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  showQuickJumper={true}
                  showTotal={(total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                  onChange={handlePageChange}
                  size="small"
                />
              </div>
            )}
          </div>
        ) : (
          // 桌面端表格布局
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              current: current,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange
            }}
            size="middle"
          />
        )}
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingStation ? '编辑收发货站点' : '新增收发货站点'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '98%' : 600}
        style={{ top: isMobile ? 20 : 100 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="站点名称"
            rules={[{ required: true, message: '请输入站点名称' }]}
          >
            <Input placeholder="请输入站点名称" />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="开启">开启</Option>
              <Option value="关闭">关闭</Option>
            </Select>
          </Form.Item>
          

          
          <Form.Item
            name="address"
            label="地址"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input placeholder="请输入地址" />
          </Form.Item>
          
          <Form.Item
            name="contact_person"
            label="联系人"
            rules={[{ required: true, message: '请输入联系人' }]}
          >
            <Input placeholder="请输入联系人" />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="固定电话"
          >
            <Input placeholder="请输入固定电话" />
          </Form.Item>
          
          <Form.Item
            name="mobile"
            label="手机号码"
          >
            <Input placeholder="请输入手机号码" />
          </Form.Item>
          
          <Form.Item name="remark" label="备注">
            <Input.TextArea 
              placeholder="请输入备注" 
              rows={3}
              showCount
              maxLength={500}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingStation ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入收发货站点"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Upload
            beforeUpload={handleFileUpload}
            accept=".csv"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>选择CSV文件</Button>
          </Upload>
          <span style={{ marginLeft: '8px', color: '#666' }}>
            支持CSV格式，第一行为表头
          </span>
        </div>

        {importData.length > 0 && (
          <>
            <Divider>字段映射</Divider>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '12px' }}>请将源文件字段映射到目标字段</h4>
              
              {/* 必填字段说明 */}
              <div style={{ 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px'
              }}>
                <Text strong style={{ color: '#52c41a' }}>必填字段说明：</Text>
                <Text style={{ marginLeft: '8px' }}>
                  站点名称、地址、联系人为必填字段，其他字段为可选字段
                </Text>
              </div>
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {Object.keys(fieldMapping).map((field, index) => {
                  const targetField = fieldMapping[field];
                  const isRequired = requiredFields.includes(targetField);
                  
                  return (
                    <div key={field} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      {/* 源文件字段 */}
                      <div style={{ 
                        flex: '1',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{ fontWeight: '500', color: '#1890ff' }}>{field}</span>
                      </div>
                      
                      {/* 映射箭头 */}
                      <div style={{ 
                        fontSize: '18px',
                        color: '#1890ff',
                        fontWeight: 'bold',
                        padding: '0 8px'
                      }}>
                        →
                      </div>
                      
                      {/* 目标字段 */}
                      <div style={{ flex: '1' }}>
                        <Select
                          value={fieldMapping[field]}
                          onChange={(value) => setFieldMapping({ ...fieldMapping, [field]: value })}
                          style={{ width: '100%' }}
                          placeholder="请选择对应字段"
                        >
                          <Option value="name">站点名称</Option>
                          <Option value="address">地址</Option>
                          <Option value="contact_person">联系人</Option>
                          <Option value="phone">固定电话</Option>
                          <Option value="mobile">手机号码</Option>
                          <Option value="status">状态</Option>
                          <Option value="remark">备注</Option>
                          <Option value="skip">不导入</Option>
                        </Select>
                      </div>
                      
                      {/* 字段状态标识 */}
                      <div style={{ 
                        width: '60px',
                        textAlign: 'center'
                      }}>
                        {targetField && (
                          <Tag color={
                            targetField === 'skip' ? 'default' : 
                            isRequired ? 'red' : 'green'
                          }>
                            {targetField === 'skip' ? '不导入' : 
                             isRequired ? '必填' : '可选'}
                          </Tag>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Divider>数据预览（前5条）</Divider>
            <Table
              dataSource={importData.slice(0, 5)}
              columns={Object.keys(fieldMapping).map(field => ({
                title: field,
                dataIndex: field,
                key: field,
              }))}
              pagination={false}
              size="small"
            />

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setImportModalVisible(false)}>
                  取消
                </Button>
                <Button type="primary" onClick={handleImport}>
                  开始导入
                </Button>
              </Space>
            </div>
          </>
        )}

        {/* 导入结果 */}
        {importResult && (
          <>
            <Divider>导入结果</Divider>
            
            {/* 结果统计 */}
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Statistic 
                  title="成功导入" 
                  value={importResult.success} 
                  valueStyle={{ color: '#3f8600' }}
                  suffix="条记录"
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="跳过记录" 
                  value={importResult.skipped} 
                  valueStyle={{ color: '#faad14' }}
                  suffix="条记录"
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="失败记录" 
                  value={importResult.failed} 
                  valueStyle={{ color: '#cf1322' }}
                  suffix="条记录"
                />
              </Col>
            </Row>
            
            {/* 错误详情 */}
            {importResult.errors.length > 0 && (
              <div style={{ 
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '4px'
              }}>
                <h4 style={{ color: '#cf1322', marginBottom: '12px' }}>详细说明：</h4>
                
                {/* 跳过记录说明 */}
                {importResult.errors.filter(error => error.reason.includes('重复记录')).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#faad14' }}>跳过记录原因：</Text>
                    <Text style={{ marginLeft: '8px' }}>
                      系统检测到重复记录（站点名称+地址+联系人组合已存在），已自动跳过
                    </Text>
                  </div>
                )}
                
                {/* 失败记录说明 */}
                {importResult.errors.filter(error => error.reason.includes('必填字段')).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#cf1322' }}>失败记录原因：</Text>
                    <Text style={{ marginLeft: '8px' }}>
                      必填字段数据不完整，无法导入
                    </Text>
                  </div>
                )}
                
                {/* 具体错误列表 */}
                <div style={{ marginTop: '12px' }}>
                  <Text strong>具体错误：</Text>
                  {importResult.errors.map((error, index) => (
                    <div key={index} style={{ 
                      color: '#cf1322', 
                      marginBottom: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#fff',
                      borderRadius: '2px'
                    }}>
                      <Text strong>第{error.row}行：</Text> {error.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* 站点详情抽屉 */}
      <Drawer
        title="站点详情"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? '100%' : 700}
        height={isMobile ? '85%' : undefined}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedStation && (
          <div>
            <Descriptions title="基本信息" bordered column={1}>
              <Descriptions.Item label="站点名称">{selectedStation.name}</Descriptions.Item>
              <Descriptions.Item label="地址">{selectedStation.address}</Descriptions.Item>
              <Descriptions.Item label="联系人">{selectedStation.contact_person}</Descriptions.Item>
              <Descriptions.Item label="固定电话">{selectedStation.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号码">{selectedStation.mobile || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={selectedStation.status === '开启' ? 'green' : 'red'}>
                  {selectedStation.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="备注">{selectedStation.remark || '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ShippingStationManagement;
